import React from 'react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import { addDoc, collection, doc, getDoc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions as firebaseFunctions } from '../../firebase';
import useAuthStore from '../../stores/authStore';
import useUserProfileStore from '../../stores/userProfileStore';
import { hasSettingsAccess, SETTINGS_SECTION_KEYS } from '../../utils/accessControl';
import { normalizeStoredSubscriptionStatus } from '../../utils/firebase/normalizeSubscriptionStatus';

const ACTIVE_SUBSCRIPTION_STATUSES = new Set([
    'active',
    'trialing',
    'past_due',
    'unpaid',
    'expiring',
]);

const CANCELED_SUBSCRIPTION_STATUSES = new Set([
    'expiring',
    'canceled',
    'cancelled',
]);

const CANCELLABLE_SUBSCRIPTION_STATUSES = new Set([
    'active',
    'trialing',
    'past_due',
    'unpaid',
]);

const COMPLETED_PAYMENT_EVENT_STATUSES = new Set([
    'COMPLETE',
    'ACTIVE',
]);

const RESTORED_SUBSCRIPTION_STATUS = 'expiring';

const EMPTY_CLAIMS_STATE = Object.freeze({
    admin: false,
    isPremiumSubscriber: false,
    subscribedYearly: false,
});

const EMPTY_BANK_DETAILS = Object.freeze({
    bank_account_holder: '',
    bank_name: '',
    bank_branch_code: '',
    bank_account_number: '',
    bank_account_type: '',
});

const DEFAULT_CANCELLATION_FEEDBACK = Object.freeze({
    rating: 5,
    reason: '',
    testimonial: '',
});

const getCallableErrorMessage = (error, fallback) => (
    error?.details?.userMessage ||
    error?.message ||
    fallback
);

const toDate = (value) => {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        return value;
    }

    if (typeof value?.toDate === 'function') {
        return value.toDate();
    }

    if (typeof value?.seconds === 'number') {
        return new Date(value.seconds * 1000);
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getTimestampMs = (value) => toDate(value)?.getTime() || 0;

const formatDateTime = (value) => {
    const date = toDate(value);

    if (!date) {
        return 'Not available';
    }

    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

const formatDateOnly = (value) => {
    const date = toDate(value);

    if (!date) {
        return 'Not available';
    }

    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(date);
};

const formatTimeOnly = (value) => {
    const date = toDate(value);

    if (!date) {
        return '';
    }

    return new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

const humanizeValue = (value, fallback = 'Not set') => {
    const normalized = String(value || '').trim();
    if (!normalized) {
        return fallback;
    }

    return normalized
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const getHistoryBadgeTone = (event) => {
    const eventStatus = String(event?.eventStatus || '').trim().toUpperCase();
    const eventKind = String(event?.kind || '').trim().toLowerCase();

    if (eventKind === 'refund' || eventStatus === 'REFUNDED') {
        return 'warning';
    }

    if (eventStatus === 'COMPLETE' || eventStatus === 'ACTIVE') {
        return 'success';
    }

    if (eventStatus === 'FAILED' || eventStatus === 'CANCELED' || eventStatus === 'CANCELLED') {
        return 'danger';
    }

    return 'neutral';
};

const isRefundEvent = (event) => {
    const eventStatus = String(event?.eventStatus || '').trim().toUpperCase();
    const eventKind = String(event?.kind || '').trim().toLowerCase();
    return eventKind === 'refund' || eventStatus === 'REFUNDED';
};

const isCompletedPaymentEvent = (event) => {
    const eventStatus = String(event?.eventStatus || '').trim().toUpperCase();
    const eventKind = String(event?.kind || '').trim().toLowerCase();
    return eventKind === 'payment' && COMPLETED_PAYMENT_EVENT_STATUSES.has(eventStatus);
};

const formatCurrency = (amount, currency) => {
    const numericAmount = Number(amount);
    const normalizedCurrency = String(currency || '').trim().toUpperCase();

    if (!Number.isFinite(numericAmount)) {
        return 'Not available';
    }

    if (!normalizedCurrency) {
        return numericAmount.toFixed(2);
    }

    if (normalizedCurrency === 'ZAR') {
        const sign = numericAmount < 0 ? '-' : '';
        const absoluteAmount = Math.abs(numericAmount);
        const formattedAmount = new Intl.NumberFormat('en-ZA', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(absoluteAmount);

        return `${sign}R${formattedAmount}`;
    }

    try {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: normalizedCurrency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(numericAmount);
    } catch {
        return `${numericAmount.toFixed(2)} ${normalizedCurrency}`;
    }
};

const normalizeBillingEvent = (event, eventId, source = 'billingEvents') => ({
    id: eventId,
    source,
    provider: String(event?.provider || '').trim().toLowerCase(),
    kind: String(event?.kind || 'payment').trim().toLowerCase(),
    eventStatus: String(event?.eventStatus || event?.status || event?.paymentStatus || '').trim().toUpperCase(),
    transactionOccurredAt: event?.transactionOccurredAt || event?.occurredAt || event?.date || event?.createdAt || null,
    createdAt: event?.createdAt || null,
    gross: Number(event?.gross || event?.amount || 0),
    fee: Number(event?.fee || 0),
    net: Number(event?.net || event?.amount || 0),
    currency: event?.currency || event?.billingCurrency || null,
    sourceCurrency: event?.sourceCurrency || null,
    transactionId: event?.transactionId || event?.payfastPaymentId || event?.stripeInvoiceId || '',
    subscriptionPlan: event?.subscriptionPlan || null,
    subscriptionStatus: event?.subscriptionStatus || null,
    paymentMethod: event?.paymentMethod || null,
    originalPaymentEventId: event?.originalPaymentEventId || null,
    isRecurring: typeof event?.isRecurring === 'boolean' ? event.isRecurring : null,
});

const fetchUserSubscriptionDetails = async (userId) => {
    if (!userId) {
        return {
            summary: null,
            billingEvents: [],
        };
    }

    const summaryRef = doc(db, 'premiumSubscribers', userId);
    const billingEventsRef = collection(db, 'premiumSubscribers', userId, 'billingEvents');

    const [summarySnapshot, billingEventsSnapshot] = await Promise.all([
        getDoc(summaryRef),
        getDocs(billingEventsRef),
    ]);

    const summary = summarySnapshot.exists()
        ? {
            id: summarySnapshot.id,
            ...summarySnapshot.data(),
        }
        : null;

    let billingEvents = billingEventsSnapshot.docs.map((docSnapshot) => (
        normalizeBillingEvent(docSnapshot.data(), docSnapshot.id)
    ));

    if (!billingEvents.length && Array.isArray(summary?.billingHistory)) {
        billingEvents = summary.billingHistory.map((event, index) => (
            normalizeBillingEvent(
                event,
                event?.eventId || event?.transactionId || `legacy-${index}`,
                'legacy'
            )
        ));
    }

    billingEvents.sort((left, right) => {
        const rightTime = getTimestampMs(right.transactionOccurredAt || right.createdAt);
        const leftTime = getTimestampMs(left.transactionOccurredAt || left.createdAt);
        return rightTime - leftTime;
    });

    return {
        summary,
        billingEvents,
    };
};

const inferBillingCycle = (paymentEvent, summary, claimsState) => {
    const candidateValues = [
        paymentEvent?.subscriptionPlan,
        summary?.subscriptionPlan,
        summary?.planName,
    ]
        .map((value) => String(value || '').trim().toLowerCase())
        .filter(Boolean);

    if (candidateValues.some((value) => /\b(year|yearly|annual|annually|12\s*month)\b/.test(value))) {
        return 'yearly';
    }

    if (candidateValues.some((value) => /\b(month|monthly|30\s*day)\b/.test(value))) {
        return 'monthly';
    }

    return claimsState?.subscribedYearly ? 'yearly' : 'monthly';
};

const addBillingCycleToDate = (startDate, billingCycle) => {
    const nextDate = new Date(startDate);

    if (billingCycle === 'yearly') {
        nextDate.setFullYear(nextDate.getFullYear() + 1);
        return nextDate;
    }

    nextDate.setMonth(nextDate.getMonth() + 1);
    return nextDate;
};

const getRestoredAccessEndDate = (summary, paymentEvent, claimsState) => {
    const currentEndDate = toDate(summary?.endDate);

    if (currentEndDate && currentEndDate.getTime() > Date.now()) {
        return currentEndDate;
    }

    const paymentDate = toDate(paymentEvent?.transactionOccurredAt || paymentEvent?.createdAt);
    if (!paymentDate) {
        return null;
    }

    const nextEndDate = addBillingCycleToDate(
        paymentDate,
        inferBillingCycle(paymentEvent, summary, claimsState)
    );

    return nextEndDate.getTime() > Date.now() ? nextEndDate : null;
};

const hasAccessClaims = (claimsState) => (
    Boolean(claimsState?.isPremiumSubscriber || claimsState?.subscribedYearly)
);

const getSubscriptionBadge = (summary, claimsState) => {
    const subscriptionStatus = String(summary?.subscriptionStatus || '').trim().toLowerCase();

    if (ACTIVE_SUBSCRIPTION_STATUSES.has(subscriptionStatus)) {
        return {
            label: 'Subscribed',
            tone: 'success',
            helper: `Billing status: ${humanizeValue(subscriptionStatus)}`,
        };
    }

    if (hasAccessClaims(claimsState)) {
        return {
            label: 'Claims Enabled',
            tone: 'warning',
            helper: 'Claims say this user has premium access, but billing is not currently active.',
        };
    }

    if (summary) {
        return {
            label: 'Not Subscribed',
            tone: 'neutral',
            helper: subscriptionStatus
                ? `Billing status: ${humanizeValue(subscriptionStatus)}`
                : 'A billing record exists, but it is not active.',
        };
    }

    return {
        label: 'No Record',
        tone: 'neutral',
        helper: 'No premium subscriber record was found for this user.',
    };
};

export default function UserSubscriptionAdminPanel({ userId }) {
    const currentUser = useAuthStore((state) => state.user);
    const role = useUserProfileStore((state) => state.role);
    const canUseAdminTools = hasSettingsAccess(role, SETTINGS_SECTION_KEYS.ADMIN_TOOLS);

    const [loading, setLoading] = React.useState(true);
    const [claimsLoading, setClaimsLoading] = React.useState(false);
    const [summary, setSummary] = React.useState(null);
    const [billingEvents, setBillingEvents] = React.useState([]);
    const [claimsState, setClaimsState] = React.useState(EMPTY_CLAIMS_STATE);
    const [emailVerified, setEmailVerified] = React.useState(null);
    const [billingError, setBillingError] = React.useState('');
    const [claimsError, setClaimsError] = React.useState('');
    const [actionMessage, setActionMessage] = React.useState('');
    const [isCanceling, setIsCanceling] = React.useState(false);
    const [isRestoringAccess, setIsRestoringAccess] = React.useState(false);
    const [isSavingClaims, setIsSavingClaims] = React.useState(false);
    const [refundingEventId, setRefundingEventId] = React.useState('');
    const [pendingRefundEventId, setPendingRefundEventId] = React.useState('');
    const [bankDetailFormVisible, setBankDetailFormVisible] = React.useState(false);
    const [bankDetailOptions, setBankDetailOptions] = React.useState([]);
    const [bankDetails, setBankDetails] = React.useState(EMPTY_BANK_DETAILS);

    const applyClaimsData = React.useCallback((resultData = {}) => {
        const currentClaims = resultData.customClaims || {};
        setClaimsState({
            admin: Boolean(currentClaims.admin),
            isPremiumSubscriber: Boolean(currentClaims.isPremiumSubscriber),
            subscribedYearly: Boolean(currentClaims.subscribedYearly),
        });
        setEmailVerified(typeof resultData.emailVerified === 'boolean' ? resultData.emailVerified : null);
    }, []);

    const fetchClaimsData = React.useCallback(async () => {
        if (!userId) {
            return {
                customClaims: {},
                emailVerified: null,
            };
        }

        const getClaims = httpsCallable(firebaseFunctions, 'userManagement-getCustomClaims');
        const result = await getClaims({ uid: userId });
        return result.data || {};
    }, [userId]);

    const loadClaims = React.useCallback(async ({ silent = false } = {}) => {
        if (!canUseAdminTools) {
            return;
        }

        if (!silent) {
            setClaimsLoading(true);
        }

        setClaimsError('');

        try {
            const claimsData = await fetchClaimsData();
            applyClaimsData(claimsData);
        } catch (error) {
            setClaimsError(error?.message || 'Unable to load current custom claims.');
        } finally {
            if (!silent) {
                setClaimsLoading(false);
            }
        }
    }, [applyClaimsData, canUseAdminTools, fetchClaimsData]);

    const loadPanelData = React.useCallback(async ({ initial = false } = {}) => {
        if (initial) {
            setLoading(true);
        }

        setBillingError('');

        const tasks = [fetchUserSubscriptionDetails(userId)];
        if (canUseAdminTools) {
            setClaimsLoading(true);
            setClaimsError('');
            tasks.push(fetchClaimsData());
        }

        const results = await Promise.allSettled(tasks);
        const billingResult = results[0];

        if (billingResult.status === 'fulfilled') {
            setSummary(billingResult.value.summary);
            setBillingEvents(billingResult.value.billingEvents);
        } else {
            setSummary(null);
            setBillingEvents([]);
            setBillingError(billingResult.reason?.message || 'Unable to load subscription details.');
        }

        if (canUseAdminTools) {
            const claimsResult = results[1];
            if (claimsResult?.status === 'fulfilled') {
                applyClaimsData(claimsResult.value);
            } else {
                setClaimsError(claimsResult?.reason?.message || 'Unable to load current custom claims.');
            }
            setClaimsLoading(false);
        }

        if (initial) {
            setLoading(false);
        }
    }, [applyClaimsData, canUseAdminTools, fetchClaimsData, userId]);

    React.useEffect(() => {
        void loadPanelData({ initial: true });
    }, [loadPanelData]);

    const subscriptionBadge = getSubscriptionBadge(summary, claimsState);
    const subscriptionStatus = String(summary?.subscriptionStatus || '').trim().toLowerCase();
    const refundedOriginalEventIds = new Set(
        billingEvents
            .filter((event) => isRefundEvent(event) && event.originalPaymentEventId)
            .map((event) => event.originalPaymentEventId)
    );
    const hasRefundHistory = billingEvents.some((event) => isRefundEvent(event));
    const restorablePaymentEvent = billingEvents.find((event) => (
        isCompletedPaymentEvent(event) &&
        !refundedOriginalEventIds.has(event.id)
    ));
    const restoredAccessEndDate = getRestoredAccessEndDate(summary, restorablePaymentEvent, claimsState);
    const billingEventsWithNet = billingEvents.filter((event) => Number.isFinite(event.net));
    const totalNetEarned = billingEventsWithNet.reduce((total, event) => total + event.net, 0);
    const totalNetCurrencies = [
        ...new Set(
            billingEventsWithNet
                .map((event) => String(event.currency || event.sourceCurrency || summary?.billingCurrency || '').trim().toUpperCase())
                .filter(Boolean)
        ),
    ];
    const totalNetCurrency = totalNetCurrencies.length === 1
        ? totalNetCurrencies[0]
        : !totalNetCurrencies.length && String(summary?.billingProvider || '').trim().toLowerCase() === 'payfast'
            ? 'ZAR'
            : '';
    const totalNetValue = totalNetCurrencies.length > 1
        ? totalNetEarned.toFixed(2)
        : formatCurrency(totalNetEarned, totalNetCurrency);

    const summaryCards = [
        {
            label: 'Total Net Earned',
            value: totalNetValue,
            helper: '',
        },
        {
            label: 'Billing Status',
            value: summary ? humanizeValue(summary.subscriptionStatus, 'Unknown') : 'No record',
            helper: summary?.endDate ? `Access until ${formatDateTime(summary.endDate)}` : 'No premium billing summary found.',
        },
        {
            label: 'Provider',
            value: humanizeValue(summary?.billingProvider, 'Unknown'),
            helper: '',
        },
        {
            label: 'Payment Method',
            value: humanizeValue(summary?.paymentMethod, 'Unknown'),
            helper: '',
        },
        {
            label: 'Billing Events',
            value: String(billingEvents.length),
            helper: '',
        },
        {
            label: 'Last Updated',
            value: formatDateTime(summary?.lastUpdated),
            helper: summary?.latestBillingEventId ? `Latest event ID: ${summary.latestBillingEventId}` : 'No latest billing event is recorded.',
        },
    ];

    if (canUseAdminTools) {
        summaryCards.push(
            {
                label: 'Premium Claim',
                value: claimsState.isPremiumSubscriber ? 'On' : 'Off',
                helper: '',
            },
            {
                label: 'Yearly Claim',
                value: claimsState.subscribedYearly ? 'On' : 'Off',
                helper: '',
            },
        );
    }

    const isManagedSubscription = Boolean(
        summary?.billingProvider ||
        summary?.payfastToken ||
        summary?.stripeCustomerId ||
        summary?.stripeSubscriptionId
    );
    const hasBillingAccess = ACTIVE_SUBSCRIPTION_STATUSES.has(subscriptionStatus);
    const hasClaimsAccess = hasAccessClaims(claimsState);
    const isSubscriptionAlreadyCanceled = CANCELED_SUBSCRIPTION_STATUSES.has(subscriptionStatus);
    const canCancelSubscription = canUseAdminTools &&
        Boolean(userId) &&
        isManagedSubscription &&
        CANCELLABLE_SUBSCRIPTION_STATUSES.has(subscriptionStatus);
    const canRestorePremiumAccess = canUseAdminTools &&
        Boolean(userId) &&
        hasRefundHistory &&
        Boolean(restorablePaymentEvent) &&
        Boolean(restoredAccessEndDate) &&
        (!hasBillingAccess || !hasClaimsAccess);
    const showRestorePremiumAccessButton = canUseAdminTools &&
        hasRefundHistory &&
        Boolean(restorablePaymentEvent);
    const cancelSubscriptionButtonLabel = isCanceling
        ? 'Canceling...'
        : isSubscriptionAlreadyCanceled
            ? 'Already Canceled'
            : 'Cancel Subscription';
    const restoreAccessButtonLabel = isRestoringAccess
        ? 'Restoring...'
        : hasBillingAccess && hasClaimsAccess
            ? 'Already Restored'
            : 'Restore Premium Access';

    const handleCancelSubscription = async () => {
        const confirmed = window.confirm(
            `Cancel the current subscription for "${userId}"? This will stop future renewals.`
        );

        if (!confirmed) {
            return;
        }

        const cancelSubscription = httpsCallable(firebaseFunctions, 'subscriptions-cancelUserSubscription');
        setIsCanceling(true);
        setActionMessage('');

        try {
            const result = await cancelSubscription({
                userId,
                ...DEFAULT_CANCELLATION_FEEDBACK,
            });
            const data = result.data || {};

            await normalizeStoredSubscriptionStatus(userId);

            const successMessage = data.message || (data.notManaged || !data.provider
                ? 'This user does not currently have a managed subscription to cancel.'
                : data.activeUntil && data.subscriptionStatus === 'expiring'
                    ? `Subscription canceled. Access remains until ${formatDateTime(data.activeUntil)}.`
                    : 'Subscription canceled successfully.');

            setActionMessage(successMessage);
            alert(successMessage);
            await loadPanelData();
        } catch (error) {
            const errorMessage = getCallableErrorMessage(
                error,
                'Unable to cancel this subscription.'
            );
            setActionMessage(errorMessage);
            alert(errorMessage);
        } finally {
            setIsCanceling(false);
        }
    };

    const handleRefundPayment = async (billingEventId) => {
        const paymentEvent = billingEvents.find((event) => event.id === billingEventId);
        const confirmed = window.confirm(
            `Refund the payment${paymentEvent?.transactionId ? ` with transaction ID "${paymentEvent.transactionId}"` : ''}?`
        );

        if (!confirmed) {
            return;
        }

        const refundTransaction = httpsCallable(firebaseFunctions, 'subscriptions-refundTransaction');
        setRefundingEventId(billingEventId);
        setActionMessage('');

        try {
            const result = await refundTransaction({
                userId,
                billingEventId,
            });
            const data = result.data || {};

            if (data.requiresBankDetails) {
                setPendingRefundEventId(billingEventId);
                setBankDetailOptions(Array.isArray(data.bankNames) ? data.bankNames : []);
                setBankDetailFormVisible(true);
                setActionMessage(data.message || 'Bank details are required before this refund can be completed.');
                return;
            }

            const successMessage = data.message ||
                (data.requiresFollowup
                    ? 'Refund completed, but provider cancellation still needs follow-up.'
                    : 'Refund completed successfully.');

            setBankDetailFormVisible(false);
            setPendingRefundEventId('');
            setBankDetails(EMPTY_BANK_DETAILS);
            setActionMessage(successMessage);
            alert(successMessage);
            await loadPanelData();
        } catch (error) {
            const errorMessage = error?.message || 'Unable to refund this payment.';
            setActionMessage(errorMessage);
            alert(errorMessage);
        } finally {
            setRefundingEventId('');
        }
    };

    const handleRestorePremiumAccess = async () => {
        if (!userId || !restorablePaymentEvent) {
            return;
        }

        if (!restoredAccessEndDate) {
            const errorMessage = 'Unable to restore access because no future end date could be worked out from the remaining payment history.';
            setActionMessage(errorMessage);
            alert(errorMessage);
            return;
        }

        const restoredBillingCycle = inferBillingCycle(restorablePaymentEvent, summary, claimsState);
        const confirmed = window.confirm(
            `Restore premium access for "${userId}" until ${formatDateTime(restoredAccessEndDate)}?\n\n` +
            'This turns paid access back on in the app, but it does not restart automatic billing with the payment provider.'
        );

        if (!confirmed) {
            return;
        }

        const setClaims = httpsCallable(firebaseFunctions, 'userManagement-setCustomClaims');
        const summaryRef = doc(db, 'premiumSubscribers', userId);
        const billingEventsRef = collection(db, 'premiumSubscribers', userId, 'billingEvents');
        const summaryUpdate = {
            subscriptionStatus: RESTORED_SUBSCRIPTION_STATUS,
            endDate: restoredAccessEndDate,
            lastUpdated: serverTimestamp(),
            manuallyRestoredAt: serverTimestamp(),
            manuallyRestoredBy: currentUser?.uid || '',
            manuallyRestoredFromEventId: restorablePaymentEvent.id,
        };

        if (!summary?.billingProvider && restorablePaymentEvent.provider) {
            summaryUpdate.billingProvider = restorablePaymentEvent.provider;
        }

        if (!summary?.paymentMethod && restorablePaymentEvent.paymentMethod) {
            summaryUpdate.paymentMethod = restorablePaymentEvent.paymentMethod;
        }

        setIsRestoringAccess(true);
        setActionMessage('');

        try {
            await setClaims({
                uid: userId,
                admin: claimsState.admin,
                isPremiumSubscriber: true,
                subscribedYearly: restoredBillingCycle === 'yearly',
            });

            await setDoc(summaryRef, summaryUpdate, { merge: true });

            await addDoc(billingEventsRef, {
                kind: 'access_restore',
                eventStatus: 'MANUAL',
                transactionOccurredAt: serverTimestamp(),
                createdAt: serverTimestamp(),
                gross: 0,
                fee: 0,
                net: 0,
                currency: restorablePaymentEvent.currency || restorablePaymentEvent.sourceCurrency || summary?.billingCurrency || null,
                provider: restorablePaymentEvent.provider || summary?.billingProvider || null,
                paymentMethod: restorablePaymentEvent.paymentMethod || summary?.paymentMethod || null,
                subscriptionPlan: restorablePaymentEvent.subscriptionPlan || summary?.subscriptionPlan || summary?.planName || null,
                subscriptionStatus: RESTORED_SUBSCRIPTION_STATUS,
                originalPaymentEventId: restorablePaymentEvent.id,
                note: 'Manual premium access restore after refund.',
                restoredByAdminUid: currentUser?.uid || null,
            });

            const successMessage =
                `Premium access restored until ${formatDateTime(restoredAccessEndDate)}. ` +
                'Future renewals may still need provider follow-up.';

            setActionMessage(successMessage);
            alert(successMessage);
            await loadPanelData();
        } catch (error) {
            const errorMessage = getCallableErrorMessage(
                error,
                'Unable to restore premium access.'
            );
            setActionMessage(errorMessage);
            alert(errorMessage);
        } finally {
            setIsRestoringAccess(false);
        }
    };

    const handleSubmitBankDetails = async () => {
        if (!pendingRefundEventId) {
            return;
        }

        const refundTransaction = httpsCallable(firebaseFunctions, 'subscriptions-refundTransaction');
        setRefundingEventId(pendingRefundEventId);
        setActionMessage('');

        try {
            const result = await refundTransaction({
                userId,
                billingEventId: pendingRefundEventId,
                bankDetails,
            });
            const data = result.data || {};
            const successMessage = data.message || 'Refund completed successfully.';

            setBankDetailFormVisible(false);
            setPendingRefundEventId('');
            setBankDetailOptions([]);
            setBankDetails(EMPTY_BANK_DETAILS);
            setActionMessage(successMessage);
            alert(successMessage);
            await loadPanelData();
        } catch (error) {
            const errorMessage = error?.message || 'Unable to finish the refund with bank details.';
            setActionMessage(errorMessage);
            alert(errorMessage);
        } finally {
            setRefundingEventId('');
        }
    };

    const handleClaimsToggle = (fieldName) => (event) => {
        setClaimsState((currentState) => ({
            ...currentState,
            [fieldName]: event.target.checked,
        }));
    };

    const handleUpdateClaims = async () => {
        const setClaims = httpsCallable(firebaseFunctions, 'userManagement-setCustomClaims');
        setIsSavingClaims(true);
        setActionMessage('');

        try {
            const result = await setClaims({
                uid: userId,
                admin: claimsState.admin,
                isPremiumSubscriber: claimsState.isPremiumSubscriber,
                subscribedYearly: claimsState.subscribedYearly,
            });

            const successMessage = result.data?.message || 'Custom claims updated successfully.';
            setActionMessage(successMessage);
            alert(successMessage);
            await loadPanelData();
        } catch (error) {
            const errorMessage = error?.message || 'Unable to update custom claims.';
            setActionMessage(errorMessage);
            alert(errorMessage);
        } finally {
            setIsSavingClaims(false);
        }
    };

    if (loading) {
        return (
            <section className="user__section">
                <div className="user__section-loading">
                    <CircularProgress size={28} />
                    <span>Loading subscription and billing details...</span>
                </div>
            </section>
        );
    }

    return (
        <section className="user__section">
            {!userId && (
                <div className="user__messages">
                    <p className="user__message user__message--error">
                        This page is missing a valid user ID, so subscription data cannot be loaded.
                    </p>
                </div>
            )}

            <div className="user__section-header">
                <div>
                    <h2 className="user__section-heading">Subscription &amp; Billing</h2>
                    <p className="user__section-copy">
                        This shows whether the user currently has paid access, which billing provider is involved,
                        and every billing event we can find for them.
                    </p>
                </div>
                <span className={`user__status-pill user__status-pill--${subscriptionBadge.tone}`}>
                    {subscriptionBadge.label}
                </span>
            </div>

            <p className="user__section-helper">{subscriptionBadge.helper}</p>

            {(billingError || claimsError || actionMessage) && (
                <div className="user__messages">
                    {billingError && <p className="user__message user__message--error">{billingError}</p>}
                    {claimsError && <p className="user__message user__message--warning">{claimsError}</p>}
                    {actionMessage && <p className="user__message user__message--info">{actionMessage}</p>}
                </div>
            )}

            <div className="user__meta-grid">
                {summaryCards.map((card) => (
                    <div key={card.label} className="user__meta-card">
                        <span className="user__meta-label">{card.label}</span>
                        <strong className="user__meta-value">{card.value}</strong>
                        {card.helper ? (
                            <span className="user__meta-helper">{card.helper}</span>
                        ) : null}
                    </div>
                ))}
            </div>

            <div className="user__action-row">
                <Button
                    variant="contained"
                    onClick={() => void loadPanelData()}
                    disabled={!userId}
                >
                    Refresh Billing
                </Button>

                {canUseAdminTools && (
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleCancelSubscription}
                        disabled={!canCancelSubscription || isCanceling}
                    >
                        {cancelSubscriptionButtonLabel}
                    </Button>
                )}

                {showRestorePremiumAccessButton && (
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleRestorePremiumAccess}
                        disabled={!canRestorePremiumAccess || isRestoringAccess}
                    >
                        {restoreAccessButtonLabel}
                    </Button>
                )}

                {canUseAdminTools && (
                    <Button
                        variant="outlined"
                        onClick={() => void loadClaims()}
                        disabled={!userId || claimsLoading}
                    >
                        {claimsLoading ? 'Refreshing Claims...' : 'Refresh Claims'}
                    </Button>
                )}
            </div>

            {showRestorePremiumAccessButton && (
                <p className="user__section-helper">
                    Use Restore Premium Access when one payment was refunded by mistake or as a duplicate.
                    It turns the user&apos;s paid access back on in the app, but it does not restart
                    automatic renewals with Stripe or PayFast.
                </p>
            )}

            {showRestorePremiumAccessButton && !restoredAccessEndDate && (
                <p className="user__section-helper">
                    This record still needs manual follow-up because the panel could not work out a future access
                    date from the remaining payment history.
                </p>
            )}

            {summary?.needsProviderCancellation && (
                <p className="user__section-helper">
                    Billing follow-up is still needed because the provider cancellation did not finish cleanly.
                </p>
            )}

            {isSubscriptionAlreadyCanceled && (
                <p className="user__section-helper">
                    This subscription has already been canceled, so future renewals are turned off.
                </p>
            )}

            {bankDetailFormVisible && (
                <div className="user__bank-form">
                    <h3 className="user__subheading">Bank details needed for this refund</h3>
                    <p className="user__section-helper">
                        PayFast asked for payout details before the refund can be completed.
                    </p>

                    <div className="user__bank-grid">
                        <input
                            placeholder="Account holder"
                            value={bankDetails.bank_account_holder}
                            onChange={(event) => setBankDetails((currentDetails) => ({
                                ...currentDetails,
                                bank_account_holder: event.target.value,
                            }))}
                        />

                        <select
                            value={bankDetails.bank_name}
                            onChange={(event) => setBankDetails((currentDetails) => ({
                                ...currentDetails,
                                bank_name: event.target.value,
                            }))}
                        >
                            <option value="">Select bank</option>
                            {bankDetailOptions.map((bank) => (
                                <option
                                    key={bank.bank_name || bank.code || bank.label}
                                    value={bank.bank_name || bank.label || ''}
                                >
                                    {bank.label || bank.bank_name}
                                </option>
                            ))}
                        </select>

                        <input
                            placeholder="Branch code"
                            value={bankDetails.bank_branch_code}
                            onChange={(event) => setBankDetails((currentDetails) => ({
                                ...currentDetails,
                                bank_branch_code: event.target.value,
                            }))}
                        />

                        <input
                            placeholder="Account number"
                            value={bankDetails.bank_account_number}
                            onChange={(event) => setBankDetails((currentDetails) => ({
                                ...currentDetails,
                                bank_account_number: event.target.value,
                            }))}
                        />

                        <select
                            value={bankDetails.bank_account_type}
                            onChange={(event) => setBankDetails((currentDetails) => ({
                                ...currentDetails,
                                bank_account_type: event.target.value,
                            }))}
                        >
                            <option value="">Select account type</option>
                            <option value="CURRENT">Current</option>
                            <option value="SAVINGS">Savings</option>
                            <option value="TRANSMISSION">Transmission</option>
                        </select>
                    </div>

                    <div className="user__action-row">
                        <Button
                            variant="contained"
                            onClick={handleSubmitBankDetails}
                            disabled={!pendingRefundEventId || refundingEventId === pendingRefundEventId}
                        >
                            {refundingEventId === pendingRefundEventId ? 'Submitting...' : 'Submit Refund Details'}
                        </Button>

                        <Button
                            variant="outlined"
                            onClick={() => {
                                setBankDetailFormVisible(false);
                                setPendingRefundEventId('');
                                setBankDetailOptions([]);
                                setBankDetails(EMPTY_BANK_DETAILS);
                            }}
                        >
                            Close
                        </Button>
                    </div>
                </div>
            )}

            <div className="user__history-header">
                <div>
                    <h3 className="user__subheading">Billing history</h3>
                    <p className="user__section-helper">
                        Refund buttons appear next to completed payment events when the record comes from the live
                        `billingEvents` collection.
                    </p>
                </div>
            </div>

            {billingEvents.length ? (
                <div className="user__history-table-wrapper">
                    <table className="user__history-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Description</th>
                                <th>Provider</th>
                                <th>Status</th>
                                <th>Payment Method</th>
                                <th className="user__history-table-th--amount">Gross</th>
                                <th className="user__history-table-th--amount">Fee</th>
                                <th className="user__history-table-th--amount">Net</th>
                                <th>Transaction</th>
                                <th className="user__history-table-th--actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {billingEvents.map((event) => {
                                const alreadyRefunded = refundedOriginalEventIds.has(event.id);
                                const isCompletedPayment = event.kind === 'payment' && event.eventStatus === 'COMPLETE';
                                const showRefundButton = canUseAdminTools &&
                                    event.source === 'billingEvents' &&
                                    isCompletedPayment &&
                                    !alreadyRefunded;
                                const eventTimestamp = event.transactionOccurredAt || event.createdAt;
                                const eventCurrency = event.currency || event.sourceCurrency || summary?.billingCurrency || '';
                                const statusTone = getHistoryBadgeTone(event);
                                const netToneClass = event.net < 0
                                    ? 'user__history-number--negative'
                                    : event.net > 0
                                        ? 'user__history-number--positive'
                                        : '';

                                return (
                                    <tr key={event.id} className="user__history-row">
                                        <td>
                                            <div className="user__history-date-cell">
                                                <strong>{formatDateOnly(eventTimestamp)}</strong>
                                                <span>{formatTimeOnly(eventTimestamp)}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="user__history-description-cell">
                                                <strong>{humanizeValue(event.kind, 'Unknown')}</strong>
                                                {event.subscriptionPlan ? (
                                                    <span className="user__history-cell-subtle">
                                                        {humanizeValue(event.subscriptionPlan)}
                                                    </span>
                                                ) : null}
                                                {event.subscriptionStatus ? (
                                                    <span className="user__history-cell-subtle">
                                                        {humanizeValue(event.subscriptionStatus)}
                                                    </span>
                                                ) : null}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="user__history-provider-cell">
                                                {humanizeValue(event.provider, 'Unknown')}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`user__history-badge user__history-badge--${statusTone}`}>
                                                {humanizeValue(event.eventStatus, 'Unknown')}
                                            </span>
                                        </td>
                                        <td>{humanizeValue(event.paymentMethod, 'Unknown')}</td>
                                        <td className="user__history-number">
                                            {formatCurrency(event.gross, eventCurrency)}
                                        </td>
                                        <td className={`user__history-number ${event.fee < 0 ? 'user__history-number--negative' : ''}`}>
                                            {formatCurrency(event.fee, eventCurrency)}
                                        </td>
                                        <td className={`user__history-number ${netToneClass}`}>
                                            <strong>{formatCurrency(event.net, eventCurrency)}</strong>
                                        </td>
                                        <td className="user__history-transaction">
                                            <div className="user__history-transaction-cell">
                                                {event.transactionId ? <code>{event.transactionId}</code> : 'Not available'}
                                                <span
                                                    className="user__history-cell-subtle user__history-event-id"
                                                    title={`Event ID: ${event.id}`}
                                                >
                                                    Event ID: {event.id}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="user__history-actions-cell">
                                            {showRefundButton ? (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => void handleRefundPayment(event.id)}
                                                    disabled={Boolean(refundingEventId)}
                                                >
                                                    {refundingEventId === event.id ? 'Refunding...' : 'Refund'}
                                                </Button>
                                            ) : alreadyRefunded ? (
                                                <span className="user__history-cell-subtle">
                                                    Refunded
                                                </span>
                                            ) : (
                                                <span className="user__history-cell-subtle">No action</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="user__empty-state">
                    No billing history was found for this user yet.
                </p>
            )}

            {canUseAdminTools && (
                <div className="user__claims-panel">
                    <div className="user__claims-header">
                        <div>
                            <h3 className="user__subheading">Custom claims</h3>
                            <p className="user__section-helper">
                                These are the auth-token flags for the user. In plain English, they decide what access
                                the app thinks this user has after they refresh their login.
                            </p>
                        </div>
                        {claimsLoading && (
                            <div className="user__claims-loading">
                                <CircularProgress size={18} />
                                <span>Loading claims...</span>
                            </div>
                        )}
                    </div>

                    <div className="user__claims-grid">
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={claimsState.isPremiumSubscriber}
                                    onChange={handleClaimsToggle('isPremiumSubscriber')}
                                />
                            )}
                            label="Premium Subscriber"
                        />

                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={claimsState.subscribedYearly}
                                    onChange={handleClaimsToggle('subscribedYearly')}
                                />
                            )}
                            label="Subscribed Yearly"
                        />
                    </div>

                    <p className="user__section-helper">
                        Email verified: {emailVerified === null ? 'Unknown' : emailVerified ? 'Yes' : 'No'}
                    </p>

                    <div className="user__action-row">
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleUpdateClaims}
                            disabled={!userId || isSavingClaims}
                        >
                            {isSavingClaims ? 'Updating...' : 'Update Claims'}
                        </Button>
                    </div>
                </div>
            )}
        </section>
    );
}
